import {
  MdAssignment,
  MdBusiness,
  MdCheckCircle,
  MdError,
  MdGavel,
  MdHelpOutline,
  MdInfo,
  MdPerson,
  MdReport,
  MdSettings,
  MdThumbUp,
} from "react-icons/md";
import { PriorityProblemBadge, normalizeProblemBadges } from "../../shared/PriorityProblemBadge";
import { priorityActionStyles } from "../../shared/priorityActions";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";
import "./PageDashboard.css";

const DOMAINE_CONFIG_PAGES = {
  ropa: "ropa",
  gouvernance: "preuves",
  soustraitance: "registre-sous-traitants",
  incidents: "incidents",
  droits: "droits",
  consentements: "consentement",
};

function scoreTone(score) {
  if (score == null) return "unknown";
  if (score >= 80) return "good";
  if (score >= 50) return "warn";
  return "bad";
}

function scoreBarColor(score) {
  const tone = scoreTone(score);
  if (tone === "good") return "#22c55e";
  if (tone === "warn") return "#f59e0b";
  if (tone === "bad") return "#ef4444";
  return "#94a3b8";
}

function globalScoreMessage(score, unknown) {
  if (unknown) {
    return "Configurez les domaines ou déclarez « Rien à déclarer » pour afficher le score global.";
  }
  if (score >= 80) {
    return `Votre indice de conformité est actuellement de ${score}%. Le niveau est satisfaisant — maintenez vos registres à jour.`;
  }
  if (score >= 50) {
    return `Votre indice de conformité est actuellement de ${score}%. Plusieurs modules nécessitent une attention pour sécuriser votre conformité.`;
  }
  return `Votre indice de conformité est actuellement de ${score}%. Des actions correctives sont nécessaires pour réduire les risques identifiés.`;
}

function ComplianceGauge({ score, unknown }) {
  const fill = unknown ? 0 : Math.max(0, Math.min(100, score));
  const color = unknown ? "#94a3b8" : scoreBarColor(score);

  return (
    <div className="dashboard-gauge" aria-label={unknown ? "Score inconnu" : `Score global ${score} sur 100`}>
      <div className="dashboard-gauge__track" />
      <div
        className="dashboard-gauge__fill"
        style={{
          background: `conic-gradient(${color} 0% ${fill}%, transparent ${fill}% 100%)`,
          WebkitMask: "radial-gradient(transparent 65%, black 66%)",
          mask: "radial-gradient(transparent 65%, black 66%)",
        }}
      />
      <div className="dashboard-gauge__value">
        <span className="dashboard-gauge__score">{unknown ? "?" : score}</span>
        <span className="dashboard-gauge__max">/ 100</span>
      </div>
    </div>
  );
}

function DomainPill({ label, score }) {
  const tone = scoreTone(score);
  const icon =
    tone === "good" ? <MdCheckCircle size={16} /> : tone === "unknown" ? <MdHelpOutline size={16} /> : <MdError size={16} />;
  const text = score == null ? `${label} INCONNU` : `${label} ${score}%`;

  return (
    <span className={`dashboard-domain-pill dashboard-domain-pill--${tone}`}>
      {icon}
      {text}
    </span>
  );
}

export default function PageDashboard({
  proofs,
  incidents,
  droits,
  sousTraitants,
  activites,
  traitements,
  pointsCollecte,
  retraits,
  domainDeclarations = {},
  onNavigate,
  priorityActions = [],
}) {
  const now = new Date();

  const rawDomains = [
    {
      key: "ropa",
      label: "ROPA (Registre)",
      subtitle: "Inventaire des traitements de données",
      icon: <MdAssignment size={18} />,
      isEmpty: traitements.length === 0,
      rawScore:
        traitements.length === 0
          ? 0
          : Math.round(
              (traitements.filter((t) => t.baseLegale && t.dureeConservation && t.mesuresSecurite.length > 0).length /
                traitements.length) *
                100
            ),
      items: [
        { label: "Traitements documentés", val: traitements.length },
        {
          label: "PIA manquants",
          val: traitements.filter((t) => !t.piaRealise && (t.risque === "Élevé" || t.risque === "Critique")).length,
          bad: traitements.filter((t) => !t.piaRealise && (t.risque === "Élevé" || t.risque === "Critique")).length > 0,
        },
        {
          label: "Transferts hors UE",
          val: traitements.filter(
            (t) =>
              t.transfertsHorsUE &&
              (!t.transfertsDetail || t.transfertsDetail.includes("à activer") || t.transfertsDetail.includes("à réaliser"))
          ).length,
          bad:
            traitements.filter(
              (t) =>
                t.transfertsHorsUE &&
                (!t.transfertsDetail || t.transfertsDetail.includes("à activer") || t.transfertsDetail.includes("à réaliser"))
            ).length > 0,
        },
      ],
    },
    {
      key: "gouvernance",
      label: "Gouvernance",
      subtitle: "Organisation interne",
      icon: <MdGavel size={18} />,
      isEmpty: proofs.length === 0 && activites.length === 0,
      rawScore: proofs.length === 0 ? 0 : Math.round((proofs.filter((p) => p.statut === "Validé").length / proofs.length) * 100),
      items: [
        {
          label: "Preuves validées",
          val: `${proofs.filter((p) => p.statut === "Validé").length} / ${proofs.length}`,
          bad: proofs.length > 0 && proofs.filter((p) => p.statut === "Validé").length !== proofs.length,
        },
        {
          label: "Non conformes",
          val: proofs.filter((p) => p.statut === "Non conforme").length,
          bad: proofs.filter((p) => p.statut === "Non conforme").length > 0,
        },
        { label: "Activités RGPD", val: activites.length },
      ],
    },
    {
      key: "soustraitance",
      label: "Sous-traitance",
      subtitle: "Contrôle des tiers",
      icon: <MdBusiness size={18} />,
      isEmpty: sousTraitants.length === 0,
      rawScore:
        sousTraitants.length === 0
          ? 0
          : Math.round((sousTraitants.filter((s) => s.dpaStatut === "Signé").length / sousTraitants.length) * 100),
      items: [
        { label: "Sous-traitants", val: sousTraitants.length },
        {
          label: "DPA signés",
          val: `${sousTraitants.filter((s) => s.dpaStatut === "Signé").length} / ${sousTraitants.length}`,
          bad: sousTraitants.length > 0 && !sousTraitants.every((s) => s.dpaStatut === "Signé" || s.dpaStatut === "Non requis"),
        },
      ],
    },
    {
      key: "incidents",
      label: "Incidents",
      subtitle: "Registre des violations",
      icon: <MdReport size={18} />,
      isEmpty: incidents.length === 0,
      rawScore:
        incidents.length === 0 ? 100 : Math.round((incidents.filter((i) => i.statut === "Clôturé").length / incidents.length) * 100),
      items: [
        {
          label: "Ouverts",
          val: incidents.filter((i) => !["Clôturé", "Sans suite"].includes(i.statut)).length,
          bad: incidents.filter((i) => !["Clôturé", "Sans suite"].includes(i.statut)).length > 0,
        },
        { label: "Notifiés CNIL", val: incidents.filter((i) => i.statut === "Notifié CNIL").length },
      ],
    },
    {
      key: "droits",
      label: "Droits",
      subtitle: "Demandes d'accès",
      icon: <MdPerson size={18} />,
      isEmpty: droits.length === 0,
      rawScore:
        droits.length === 0 ? 100 : Math.round((droits.filter((d) => d.statut === "Complétée").length / droits.length) * 100),
      items: [
        {
          label: "En cours",
          val: droits.filter((d) => ["Reçue", "En cours"].includes(d.statut)).length,
          bad: droits.filter((d) => ["Reçue", "En cours"].includes(d.statut)).length > 5,
        },
        {
          label: "Taux traitement",
          val: `${droits.length === 0 ? 100 : Math.round((droits.filter((d) => d.statut === "Complétée").length / droits.length) * 100)}%`,
        },
      ],
    },
    {
      key: "consentements",
      label: "Consentements",
      subtitle: "Points de collecte",
      icon: <MdThumbUp size={18} />,
      isEmpty: pointsCollecte.length === 0 && retraits.length === 0,
      rawScore:
        pointsCollecte.length === 0
          ? 50
          : Math.round(
              (pointsCollecte.length >= 3 ? 50 : 0) +
                (retraits.filter((r) => r.statut === "Traitée").length / Math.max(retraits.length, 1)) * 50
            ),
      items: [
        { label: "Documentés", val: pointsCollecte.length },
        {
          label: "Retraits traités",
          val: `${retraits.length === 0 ? 100 : Math.round((retraits.filter((r) => r.statut === "Traitée").length / retraits.length) * 100)}%`,
        },
      ],
    },
  ];

  const domaines = rawDomains.map((d) => {
    const declaredNothing = !!domainDeclarations[d.key];
    const displayScore = d.isEmpty && declaredNothing ? 100 : d.isEmpty ? null : d.rawScore;
    return { ...d, displayScore, declaredNothing, configPage: DOMAINE_CONFIG_PAGES[d.key] };
  });

  const knownScores = domaines.filter((d) => d.displayScore != null).map((d) => d.displayScore);
  const globalScore = knownScores.length > 0 ? Math.round(knownScores.reduce((a, b) => a + b, 0) / knownScores.length) : null;
  const globalUnknown = knownScores.length === 0;

  const hasPriorityIssues =
    proofs.filter((p) => p.statut === "Non conforme").length > 0 ||
    sousTraitants.filter((s) => s.horsEEE && s.sccRequis && !["Activées (DPA)", "Activées (avenant)"].includes(s.sccStatut)).length > 0 ||
    incidents.filter((i) => !["Clôturé", "Sans suite"].includes(i.statut)).length > 0 ||
    droits.filter((d) => d.dateEcheance && new Date(d.dateEcheance) < now && !["Complétée", "Refusée"].includes(d.statut)).length > 0;

  return (
    <PageShell>
      <PageHeader
        eyebrow="Conformité RGPD"
        title="Tableau de bord conformité"
        actions={
          <button type="button" className="page-shell__btn page-shell__btn--ghost" onClick={() => onNavigate?.("dashboard-config")}>
            <MdSettings size={16} />
            Config. scores
          </button>
        }
      />

      <PageContent>
        <section className="dashboard-hero">
          <div className="dashboard-hero__inner">
            <ComplianceGauge score={globalScore ?? 0} unknown={globalUnknown} />
            <div className="dashboard-hero__summary">
              <h2 className="dashboard-hero__heading">Score global de conformité</h2>
              <p className="dashboard-hero__text">{globalScoreMessage(globalScore ?? 0, globalUnknown)}</p>
              <div className="dashboard-domain-pills">
                {domaines.map((d) => (
                  <DomainPill key={d.key} label={d.label.toUpperCase()} score={d.displayScore} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="dashboard-grid">
          {domaines.map((dom) => {
            const sc = dom.displayScore;
            const isUnknown = sc == null;
            const tone = scoreTone(sc);
            const showEmptyCallout = dom.isEmpty && !dom.declaredNothing;

            return (
              <article key={dom.key} className={`dashboard-card${isUnknown ? " dashboard-card--unknown" : ""}`}>
                <div className="dashboard-card__head">
                  <div>
                    <h3 className="dashboard-card__title">{dom.label}</h3>
                    <p className="dashboard-card__subtitle">{dom.subtitle}</p>
                  </div>
                  {isUnknown ? (
                    <span className="dashboard-card__badge">Inconnu</span>
                  ) : (
                    <span className={`dashboard-card__score dashboard-card__score--${tone}`}>{sc}%</span>
                  )}
                </div>

                <div className="dashboard-card__progress">
                  <div
                    className="dashboard-card__progress-bar"
                    style={{ width: `${isUnknown ? 0 : sc}%`, background: scoreBarColor(sc) }}
                  />
                </div>

                {showEmptyCallout && (
                  <div className="dashboard-card__callout">
                    <span className="dashboard-card__callout-label">
                      <MdInfo size={18} />
                      Non renseigné
                    </span>
                    <div className="dashboard-card__callout-actions">
                      <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => onNavigate?.("dashboard-config")}>
                        Rien à déclarer
                      </button>
                      <button type="button" className="dashboard-btn dashboard-btn--primary" onClick={() => onNavigate?.(dom.configPage)}>
                        Configurer
                      </button>
                    </div>
                  </div>
                )}

                <div className={`dashboard-card__metrics${showEmptyCallout ? " dashboard-card__metrics--dimmed" : ""}`}>
                  {dom.items.map((item) => (
                    <div key={item.label} className="dashboard-card__metric">
                      <span className="dashboard-card__metric-label">{item.label}</span>
                      <span className={`dashboard-card__metric-value${item.bad ? " dashboard-card__metric-value--bad" : ""}`}>{item.val}</span>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>

        <section className="dashboard-priority">
          <h2 className="dashboard-priority__title">Actions prioritaires</h2>
          <div className="dashboard-priority__list">
            {priorityActions.slice(0, 8).map((a, i) => {
              const sty = priorityActionStyles[a.badgeTone] || priorityActionStyles.haute;
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "12px 16px",
                    background: sty.rowBg,
                    borderRadius: 12,
                    border: `1px solid ${sty.rowBorder}`,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      padding: "2px 8px",
                      borderRadius: 99,
                      background: sty.pillBg,
                      color: sty.pillColor,
                      border: `1px solid ${sty.pillBorder}`,
                      flexShrink: 0,
                    }}
                  >
                    {a.badgeText}
                  </span>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                      {normalizeProblemBadges(a).map((b, bi) => (
                        <PriorityProblemBadge key={bi} variant={b.variant} text={b.text} />
                      ))}
                      {a.module && <span style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>{a.module}</span>}
                    </div>
                    <span style={{ fontSize: 14, color: "#374151" }}>{a.label}</span>
                  </div>
                </div>
              );
            })}

            {!hasPriorityIssues && priorityActions.length === 0 && (
              <div className="dashboard-priority__success">
                <div className="dashboard-priority__success-icon">
                  <MdCheckCircle size={24} />
                </div>
                <div>
                  <h4 className="dashboard-priority__success-title">Tout est sous contrôle</h4>
                  <p className="dashboard-priority__success-text">
                    Aucune action prioritaire en suspens — bonne conformité ! Continuez à maintenir vos registres à jour.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      </PageContent>
    </PageShell>
  );
}
